import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1120SSeparatelyStatedIncomeItems = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Separately Stated Income Items for S Corporations | Form 1120-S"
          description="Learn how separately stated income items work for S corporations, including Schedule K-1 reporting, IRS rules, and how they affect Form 1120-S shareholders."
          keywords=""
          canonical="resources/business-tax/form-1120-s-separately-stated-income-items-of-an-s-corporation/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Separately Stated Income Items of an S Corporation — Complete Guide
          for{' '}
          <span className="highlight-container">
            <span className="highlight">Form 1120-S</span>
          </span>{' '}
          Filers
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  component="article"
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/business-tax/form-1120-s-separately-stated-income-items-of-an-s-corporation/"
                  />
                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-25T09:00:00-05:00"
                      >
                        Aug 25, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-25T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 25, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    component="h2"
                    itemProp="headline"
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 0.8,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Separately Stated Income Items (Form 1120-S) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Understand what separately stated income items are
                        </strong>{' '}
                        and why the IRS requires S corporations to report them
                        separately on Form 1120-S, Schedule K, and Schedule K-1.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Learn the most common separately stated items,
                        </strong>{' '}
                        where they appear on Schedule K-1, and how they affect
                        shareholder taxes, basis, deductions, and credits.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Avoid common reporting mistakes
                        </strong>{' '}
                        by following IRS guidance and best practices for
                        accurately reporting separately stated income items.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When an S corporation files its annual return, not all income
                  and deductions are treated the same way. Certain financial
                  activities must be reported individually so shareholders can
                  apply the correct tax treatment on their personal returns.
                  These are known as{' '}
                  <span style={{ fontWeight: 600 }}>
                    separately stated income items
                  </span>
                  , and they play a major role in accurate reporting tied to{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff', fontWeight: 600 }}
                  >
                    Form 1120-S
                  </Link>
                  , <span style={{ fontWeight: 600 }}>Schedule K</span> and{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/f1120ssk.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff', fontWeight: 600 }}
                  >
                    Schedule K-1
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  According to{' '}
                  <Link
                    href="https://www.irs.gov/instructions/i1120ssk"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    official IRS guidance in the Schedule K-1 Instructions (Form
                    1120-S)
                  </Link>
                  , certain income, deductions, credits, and adjustments must be
                  reported separately so shareholders can apply individual tax
                  limitations correctly.
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What Are Separately Stated Income Items?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why the IRS Requires Separate Reporting
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#types" sx={{ color: '#0202ff' }}>
                          Common Types of Separately Stated Income Items
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where Separately Stated Items Appear on Schedule K-1
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How Separately Stated Items Affect Shareholder Taxes
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#interaction" sx={{ color: '#0202ff' }}>
                          Interaction with Stock and Debt Basis
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Reporting Mistakes to Avoid
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#best" sx={{ color: '#0202ff' }}>
                          Best Practices for Managing Separately Stated Items
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What Are Separately Stated Income Items?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Separately stated income items are categories of income, gain,
                  loss, deduction, or credit that cannot be grouped into
                  ordinary business income. Instead, they are reported
                  individually to preserve their tax character when passed to
                  shareholders.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  These items are separated because they may affect each
                  shareholder differently depending on personal tax
                  circumstances, including:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Capital gain tax treatment</li>
                  <li>Passive activity rules</li>
                  <li>Contribution limitations</li>
                  <li>Investment income calculations</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The IRS requires this separation to ensure accurate individual
                  reporting rather than applying a single tax treatment to all
                  income.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="why"
                >
                  Why the IRS Requires Separate Reporting
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  S corporations operate under pass-through taxation, meaning
                  shareholders report business activity directly on their
                  personal returns. However, not all income categories follow
                  the same rules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  IRS instructions for Form 1120-S explain that certain items
                  must be disclosed separately because they may:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Receive different tax rates</li>
                  <li>Be subject to individual-level limits</li>
                  <li>Affect shareholder basis differently</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Without separate reporting, shareholders could miscalculate
                  taxable income or incorrectly apply deductions.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="types"
                >
                  Common Types of Separately Stated Income Items
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Although the exact categories vary by corporation, several
                  items frequently appear separately on Schedule K-1.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Interest and Dividend Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Investment-related income is reported separately so
                  shareholders can apply investment income rules at the
                  individual level.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Capital Gains and Losses
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Asset sales often produce capital gains or losses that follow
                  different tax rates than ordinary business income.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Section 1231 Gains and Losses
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Transactions involving business property require special
                  treatment and therefore must be reported separately.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Charitable Contributions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Charitable deductions pass through to shareholders but are
                  subject to personal deduction limitations.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Tax-Exempt Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Certain income increases shareholder basis without generating
                  taxable income, which is why it must remain separately stated.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Credits and Special Adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Credits or adjustments tied to specific tax rules are reported
                  separately to ensure they are applied correctly on individual
                  returns.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="where"
                >
                  Where Separately Stated Items Appear on Schedule K-1
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Separately stated items are listed in designated sections of
                  Schedule K-1 issued by the S corporation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Important points:{' '}
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Ordinary business income is reported separately from
                    individually stated items.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Each item retains its original tax character.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Shareholders must combine K-1 data with their personal tax
                    situation to determine final tax outcomes.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The IRS emphasizes that the Schedule K-1 shows allocated
                  amounts — not the final taxable result.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How Separately Stated Items Affect Shareholder Taxes
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Separately stated items influence several areas of a
                  shareholder’s tax return, including:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>Capital gain treatment</li>
                  <li style={{ marginBottom: 12 }}>Deduction eligibility</li>
                  <li style={{ marginBottom: 12 }}>
                    Passive activity income calculations
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Net investment income considerations
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Because each shareholder’s situation is different, separate
                  reporting ensures accurate application of individual tax
                  rules.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="interaction"
                >
                  Interaction with Stock and Debt Basis
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Separately stated income and deduction items directly affect
                  shareholder basis.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Income items generally increase stock basis.</li>
                  <li>Deductions and losses reduce basis.</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Maintaining accurate basis tracking ensures distributions and
                  loss deductions are reported correctly.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common Reporting Mistakes to Avoid
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Many shareholders make errors when handling separately stated
                  items, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Combining separately stated income with ordinary income
                  </li>
                  <li>Ignoring individual-level tax limitations</li>
                  <li>Misinterpreting tax-exempt income</li>
                  <li>Failing to track basis adjustments properly</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Careful review of Schedule K-1 helps avoid these issues.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="best"
                >
                  Best Practices for Managing Separately Stated Items
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To maintain compliance:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Review Schedule K-1 line by line.</li>
                  <li>
                    Keep accurate records of contributions and asset
                    transactions.
                  </li>
                  <li>Track shareholder basis annually.</li>
                  <li>
                    Apply individual tax limits before claiming deductions.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Using{' '}
                  <Link
                    href="https://www.irs.gov/instructions/i1120ssk"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS guidance from Schedule K-1 Instructions (Form 1120-S)
                  </Link>{' '}
                  can help ensure interpretation of separately stated items.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to simplify your S corporation reporting?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              E-file your Form 1120-S with TaxZerone using guided steps,
              built-in accuracy checks, and secure IRS-authorized submission.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120sstatedincome`}
              >
                File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1120SSeparatelyStatedIncomeItems;
